import { LandTitleDto } from "@cradua/data";
import { createReducer, on, Action } from "@ngrx/store";
import { EntityState, EntityAdapter, createEntityAdapter } from "@ngrx/entity";

import * as AcquisitionActions from "./acquisition.actions";

export const ACQUISITION_FEATURE_KEY = "acquisition";

export interface AcquisitionState extends EntityState<LandTitleDto> {
  selectedAcquisitionId?: string | number; // which Acquisition record has been selected
  loaded: boolean; // has the Acquisition list been loaded
  error?: string | null; // last none error (if any)
}

export interface AcquisitionPartialState {
  readonly [ACQUISITION_FEATURE_KEY]: AcquisitionState;
}

export const acquisitionAdapter: EntityAdapter<LandTitleDto> =
  createEntityAdapter<
    LandTitleDto
  >({
    selectId: (Acquisition) => Acquisition.id,
    sortComparer: sortByName,
  });

export function sortByName(a: LandTitleDto, b: LandTitleDto): number {
  return new Date(b.dateCreated).getTime() - new Date(a.dateCreated).getTime();
}

export const initialState: AcquisitionState = acquisitionAdapter
  .getInitialState(
    {
      // set initial required properties
      loaded: false,
      error: null,
      selectedAcquisitionId: null,
    },
  );

const acquisitionReducer = createReducer(
  initialState,
  on(AcquisitionActions.loadAcquisition, (state) => ({
    ...state,
    loaded: true,
    error: null,
  })),
  on(
    AcquisitionActions.loadAcquisitionSuccess,
    (state, { Acquisition }) =>
      acquisitionAdapter.setAll(Acquisition, {
        ...state,
        loaded: false,
      }),
  ),
  on(AcquisitionActions.loadAcquisitionFailure, (state, { error }) => ({
    ...state,
    loaded: false,
    error,
  })),
  on(AcquisitionActions.fetchAcquisitionRecord, (state) => ({
    ...state,
    loaded: true,
    error: null,
  })),
  on(
    AcquisitionActions.fetchAcquisitionRecordSuccess,
    (state, { record }) =>
      acquisitionAdapter.setAll(record, {
        ...state,
        loaded: false,
      }),
  ),
  on(AcquisitionActions.fetchAcquisitionRecordFailure, (state, { error }) => ({
    ...state,
    loaded: false,
    error,
  })),

  on(AcquisitionActions.fetchByIdAcquisitionRecord, (state) => ({
    ...state,
    loaded: true,
    error: null,
  })),
  on(
    AcquisitionActions.fetchByIdAcquisitionRecordSuccess,
    (state, { record }) =>
      acquisitionAdapter.setAll(record, {
        ...state,
        loaded: false,
      }),
  ),
  on(AcquisitionActions.fetchByIdAcquisitionRecordFailure, (state, { error }) => ({
    ...state,
    loaded: false,
    error,
  })),

  on(AcquisitionActions.addAcquisition, (state) => ({
    ...state,
    loaded: true,
    error: null,
  })),
  on(
    AcquisitionActions.addAcquisitionSuccess,
    (state, { newAcquisition }) =>
      acquisitionAdapter.addOne(newAcquisition, {
        ...state,
        loaded: false,
      }),
  ),
  on(AcquisitionActions.addAcquisitionFailure, (state, { error }) => ({
    ...state,
    loaded: false,
    error,
  })),
  on(AcquisitionActions.updateAcquisition, (state) => ({
    ...state,
    loaded: true,
    error: null,
  })),
  on(
    AcquisitionActions.updateAcquisitionSuccess,
    (state, { updateAcquisition }) =>
      acquisitionAdapter.upsertOne(updateAcquisition, {
        ...state,
        loaded: false,
      }),
  ),
  on(AcquisitionActions.updateAcquisitionFailure, (state, { error }) => ({
    ...state,
    loaded: false,
    error,
  })),
  // search correspondence

  on(AcquisitionActions.searchAcquisitionRecord, (state, { query }) => {
    return {
      ...state,
      selectedAcquisitionId: query,
      loaded: false,
      error: null,
    };
  }),
  on(AcquisitionActions.searchAcquisitionIdRecord, (state, { query }) => {
    return {
      ...state,
      selectedAcquisitionId: query,
      loaded: false,
      error: null,
    };
  }),
  on(AcquisitionActions.removeAcquisition, (state) => ({
    ...state,
    loaded: true,
    error: null,
  })),
  on(
    AcquisitionActions.removeAcquisitionSuccess,
    (state, { id }) =>
      acquisitionAdapter.removeOne(id, { ...state, loaded: false }),
  ),
  on(AcquisitionActions.removeAcquisitionFailure, (state, { error }) => ({
    ...state,
    loaded: false,
    error,
  })),

  on(AcquisitionActions.clearAcquisition, (state) => ({
    ...state,
    loaded: true,
    error: null,
  })),
  on(
    AcquisitionActions.clearAcquisitionSuccess,
    (state, { Acquisition }) =>
      acquisitionAdapter.setAll(Acquisition, {
        ...state,
        loaded: false,
      }),
  ),
);

export function reducer(state: AcquisitionState | undefined, action: Action) {
  return acquisitionReducer(state, action);
}
